import { pack, unpack } from 'msgpackr';
import { logError } from '../config/errorlogger';

type MessageHandler = (event: string, data: unknown) => void;
type WsRole = "admin" | "agent";

export const WS_RECONNECTED = "ws:reconnected";

export class WsClient {
  private ws: WebSocket | null = null;
  private handlers = new Map<string, Set<MessageHandler>>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private shouldConnect = false;
  private url = "";
  private role: WsRole = "admin";
  private hasOpened = false;
  private isOpening = false;
  private visibilityHandler: (() => void) | null = null;
  private lastPayload = new Map<string, unknown>();
  private refreshSession: () => Promise<unknown> = () => Promise.resolve();
  private refreshedSinceAuth = false;

  connect(url: string, role: WsRole, refreshSession: () => Promise<unknown>) {
    this.shouldConnect = true;
    this.url = url;
    this.role = role;
    this.refreshSession = refreshSession;
    this._open();
    this._registerVisibility();

    return () => {
      this.shouldConnect = false;
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      this.ws?.close(1000, "Client disconnect");
      this.ws = null;
      this.isOpening = false;
      this._unregisterVisibility();
    };
  }

  private _registerVisibility() {
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible' && this.shouldConnect) {
        if (
          !this.ws ||
          this.ws.readyState === WebSocket.CLOSED ||
          this.ws.readyState === WebSocket.CLOSING
        ) {
          if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
          }
          this.isOpening = false;
          this._open();
        }
      }
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private _unregisterVisibility() {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  private _open() {
    if (this.isOpening) return;
    if (this.ws?.readyState === WebSocket.OPEN) return;

    this.isOpening = true;
    const ws = new WebSocket(`${this.url}?role=${this.role}`);
    this.ws = ws;
    ws.binaryType = 'arraybuffer';

    ws.onopen = () => {
      this.isOpening = false;
      if (this.hasOpened) {
        this.handlers.get(WS_RECONNECTED)?.forEach((fn) => fn(WS_RECONNECTED, undefined));
      }
      this.hasOpened = true;
    };

    ws.onmessage = (e) => {
      this.refreshedSinceAuth = false;
      try {
        const { event, data } = unpack(new Uint8Array(e.data));
        this.lastPayload.set(event, data);
        this.handlers.get(event)?.forEach((fn) => fn(event, data));
      } catch (error) {
        logError(error, { component: 'WsClient', action: 'decode_message' });
      }
    };

    ws.onclose = (e) => {
      this.isOpening = false;
      if (!this.shouldConnect) return;
      if (e.code === 4001 && !this.refreshedSinceAuth) {
        this.refreshedSinceAuth = true;
        this.refreshSession().then(
          () => this.shouldConnect && this._open(),
          () => { this.shouldConnect = false; },
        );
        return;
      }
      if (e.code === 4001 || e.code === 4008) {
        this.shouldConnect = false;
        return;
      }
      if (e.code === 1000) return; // Clean disconnect

      if (document.visibilityState === 'hidden') return;

      this.reconnectTimer = setTimeout(() => this._open(), 3000);
    };

    ws.onerror = () => {
      this.isOpening = false;
      ws.close();
    };
  }

  disconnect() {
    this.shouldConnect = false;
    this.isOpening = false;
    this.hasOpened = false;
    this.lastPayload.clear();
    this._unregisterVisibility();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close(1000, "Client disconnect");
    this.ws = null;
  }

  send(event: string, data: unknown) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(pack({ event, data }));
  }

  on(event: string, handler: MessageHandler, replayLast = true) {
    if (!this.handlers.has(event)) this.handlers.set(event, new Set());
    this.handlers.get(event)!.add(handler);
    if (!replayLast) return;

    // Replay last known value so late-mounting components
    // don't wait for the next server tick
    const last = this.lastPayload.get(event);
    if (last !== undefined) handler(event, last);
  }

  off(event: string, handler: MessageHandler) {
    this.handlers.get(event)?.delete(handler);
  }
}

export const wsClient = new WsClient();